import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FocusManagerService } from '@sc/feature-focus-manager';
import { ActionDialogComponent, DialogComponent } from '../../components';
import {
  ActionDialogType,
  AlertDialogData,
  DialogButtonsChoice,
  DialogButtonsConfig,
} from '../../types';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  constructor(
    public dialog: MatDialog,
    private readonly focusManager: FocusManagerService,
    @Inject(DOCUMENT) private readonly doc: Document
  ) {
    this.dialog.afterOpened.asObservable().subscribe(() => {
      setTimeout(() => {
        const dialogQuery =
          'mat-dialog-container[role=dialog], mat-bottom-sheet-container[role=dialog]';

        const allMatDialogs = this.doc.querySelectorAll(dialogQuery);

        allMatDialogs.forEach((matDialog) => {
          matDialog.removeAttribute('tabindex');

          const matDialogTitle = matDialog.querySelectorAll('h1');

          matDialogTitle?.forEach((title) => {
            if (title?.hasAttribute('mat-dialog-title')) {
              matDialog.setAttribute(
                'aria-labelledby',
                title?.getAttribute('id')
              );
            }
          });
        });

        this.focusManager.focusFirstElementAllowed(dialogQuery);
      }, 0);
    });
  }

  showDialog<DataType, dialogComponent>(
    dialogComponent,
    data: DataType,
    customClassName?: string
  ) {
    return this.dialog
      .open<dialogComponent, DataType>(dialogComponent, {
        data: data,
        panelClass: ['sc-scrollable-dialog', customClassName],
        autoFocus: false,
        disableClose: true,
      })
      .afterClosed();
  }

  showInfo(
    title: string,
    description: string,
    useButtons?: DialogButtonsConfig[]
  ) {
    return this.dialog
      .open<DialogComponent, AlertDialogData, DialogButtonsChoice>(
        DialogComponent,
        {
          data: { type: 'INFO_ALERT', title, description, useButtons },
          autoFocus: false,
        }
      )
      .afterClosed();
  }

  showWarn(
    title: string,
    description: string,
    useButtons?: DialogButtonsConfig[]
  ) {
    return this.dialog
      .open<DialogComponent, AlertDialogData, DialogButtonsChoice>(
        DialogComponent,
        {
          data: { type: 'WARN_ALERT', title, description, useButtons },
          autoFocus: false,
        }
      )
      .afterClosed();
  }

  showSuccess(
    title: string,
    description: string,
    useButtons?: DialogButtonsConfig[]
  ) {
    return this.dialog
      .open<DialogComponent, AlertDialogData, DialogButtonsChoice>(
        DialogComponent,
        {
          data: { type: 'SUCCESS_ALERT', title, description, useButtons },
          autoFocus: false,
        }
      )
      .afterClosed();
  }

  showError(
    title: string,
    description: string,
    useButtons?: DialogButtonsConfig[]
  ) {
    return this.dialog
      .open<DialogComponent, AlertDialogData, DialogButtonsChoice>(
        DialogComponent,
        {
          data: { type: 'ERROR_ALERT', title, description, useButtons },
          autoFocus: false,
        }
      )
      .afterClosed();
  }

  showActionDialog(dataParams: ActionDialogType) {
    return this.dialog
      .open<ActionDialogComponent, ActionDialogType>(ActionDialogComponent, {
        data: dataParams,
        autoFocus: false,
      })
      .afterClosed();
  }
}
